import { useApolloClient } from "@apollo/client";
import { useUser } from "@dashboard/auth";
import { DashboardCard } from "@dashboard/components/Card";
import { DashboardModal } from "@dashboard/components/Modal";
import {
  ProductLabelsDataDocument,
  ProductLabelsDataQuery,
  ProductLabelsDataQueryVariables,
  useLabelQueueDeleteMutation,
  useLabelQueueSaveMutation,
  useLabelQueuesQuery,
  useProductLabelsDataQuery,
  useVariantBatchNumberCreateMutation,
} from "@dashboard/graphql";
import useNotifier from "@dashboard/hooks/useNotifier";
import { LabelPrintDialog } from "@dashboard/labels/LabelPrintDialog";
import { LabelData } from "@dashboard/labels/types";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Box, Button, Input, Text } from "@saleor/macaw-ui-next";
import { ChevronDown, ChevronUp, Save } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useHistory, useLocation } from "react-router";

import { BatchFormDialog, BatchFormValues, toDateTimeLocalValue } from "../../BatchFormDialog";
import { batchMessages } from "../../batchMessages";
import { LabelQueue, LabelQueueEntry, MAX_LABEL_COPIES } from "../../LabelQueue";
import { LabelSavedQueues, SavedLabelQueue } from "../../LabelSavedQueues";
import { LabelVariantHit, LabelVariantSearch } from "../../LabelVariantSearch";
import {
  labelPrintingBatchesPath,
  labelPrintingHelpPath,
  LabelPrintingLocationState,
} from "../../urls";
import { messages } from "./messages";

type LabelProduct = NonNullable<ProductLabelsDataQuery["products"]>["edges"][number]["node"];
type LabelVariant = NonNullable<LabelProduct["variants"]>[number];

// A product without variants prints one "product label"; it is queued under
// its own id, the way the label data below keys it.
const hitFromProduct = (product: LabelProduct, variant?: LabelVariant): LabelVariantHit => {
  const price = variant?.channelListings?.[0]?.price;

  return {
    variantId: variant?.id ?? product.id,
    productId: product.id,
    productName: product.name,
    variantName: variant?.name ?? "",
    sku: variant?.sku ?? "",
    batchNumber: variant?.latestBatchNumber ?? null,
    price: price ? { amount: price.amount, currency: price.currency } : null,
    thumbnailUrl: product.thumbnail?.url ?? null,
  };
};

const hitsFromProduct = (product: LabelProduct): LabelVariantHit[] =>
  product.variants?.length
    ? product.variants.map(variant => hitFromProduct(product, variant))
    : [hitFromProduct(product)];

const toLabelData = (product: LabelProduct, variant?: LabelVariant): LabelData => {
  const productImages = (product.media ?? [])
    .filter(media => media.type === "IMAGE")
    .map(media => media.url);
  const variantImages = (variant?.media ?? [])
    .filter(media => media.type === "IMAGE")
    .map(media => media.url);
  const imageUrls = Array.from(
    new Set([...variantImages, ...productImages, product.thumbnail?.url].filter(Boolean)),
  ) as string[];
  const price = variant?.channelListings?.[0]?.price;

  return {
    id: variant?.id ?? product.id,
    productId: product.id,
    name: variant?.name ? `${product.name} — ${variant.name}` : product.name,
    sku: variant?.sku ?? "",
    batchNumber: variant?.latestBatchNumber ?? null,
    price: price ? { amount: price.amount, currency: price.currency } : null,
    imageUrl: imageUrls[0] ?? null,
    imageUrls,
    storefrontUrl: product.storefrontUrl ?? null,
  };
};

export const LabelPrintingOverview = () => {
  const intl = useIntl();
  const notify = useNotifier();
  const history = useHistory();
  const location = useLocation<LabelPrintingLocationState | undefined>();
  const arrival = location.state;
  const { user } = useUser();
  const isAdmin = !!user?.isSuperuser;

  // Newest first, so a scanned item lands at the top where the eye already is.
  const [queue, setQueue] = useState<LabelQueueEntry[]>([]);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const [batchDialogEntry, setBatchDialogEntry] = useState<LabelQueueEntry | null>(null);
  const [batchError, setBatchError] = useState<string | undefined>();
  const [mintBatchNumber, mintBatchNumberResult] = useVariantBatchNumberCreateMutation();

  // Saved queues (shared by staff). Opening one waits for its products'
  // label data before it fills the queue, like an arrival from a product page.
  const [isSavedQueuesOpen, setSavedQueuesOpen] = useState(false);
  const [loadingQueueId, setLoadingQueueId] = useState<string | null>(null);
  const apolloClient = useApolloClient();
  const [loadedQueueName, setLoadedQueueName] = useState<string | null>(null);
  const [saveName, setSaveName] = useState<string | null>(null);
  const [deletingQueueId, setDeletingQueueId] = useState<string | null>(null);
  const labelQueues = useLabelQueuesQuery();
  const savedQueues = labelQueues.data?.labelQueues ?? [];
  const [saveQueue, saveQueueResult] = useLabelQueueSaveMutation();
  const [deleteQueue] = useLabelQueueDeleteMutation();

  const productIds = useMemo(() => {
    const ids = new Set(queue.map(entry => entry.productId));

    for (const id of arrival?.productIds ?? []) {
      ids.add(id);
    }

    if (arrival?.pickProductId) {
      ids.add(arrival.pickProductId);
    }

    return Array.from(ids).sort();
  }, [queue, arrival]);

  const { data, previousData, refetch } = useProductLabelsDataQuery({
    variables: { ids: productIds },
    skip: productIds.length === 0,
  });
  const productsById = useMemo(
    () =>
      new Map(
        (mapEdgesToItems((data ?? previousData)?.products) ?? []).map(product => [
          product.id,
          product,
        ]),
      ),
    [data, previousData],
  );

  // Products and variants handed over by a product page or the product list
  // are queued once their data has loaded. A product page with several
  // variants doesn't queue them all: it offers them to pick from instead.
  const arrivalApplied = useRef(false);

  useEffect(() => {
    if (arrivalApplied.current || !arrival || !data) {
      return;
    }

    const wanted = arrival.pickProductId ? [arrival.pickProductId] : (arrival.productIds ?? []);
    const products = wanted.map(id => productsById.get(id)).filter(Boolean) as LabelProduct[];

    if (products.length < wanted.length) {
      return;
    }

    arrivalApplied.current = true;

    const variantIds = new Set(arrival.variantIds ?? []);
    const hits = products.flatMap(product => {
      const all = hitsFromProduct(product);

      if (variantIds.size > 0) {
        return all.filter(hit => variantIds.has(hit.variantId));
      }

      if (arrival.pickProductId) {
        return all.length === 1 ? all : [];
      }

      return all;
    });

    if (hits.length > 0) {
      setQueue(hits.map(hit => ({ ...hit, quantity: 1 })));
    }
  }, [arrival, data, productsById]);

  // Opening a saved queue fetches its products' label data first, so every
  // row has its name, SKU and batch; variants deleted since are left out.
  const openSavedQueue = async (saved: SavedLabelQueue, mode: "replace" | "append") => {
    setLoadingQueueId(saved.id);

    const result = await apolloClient
      .query<ProductLabelsDataQuery, ProductLabelsDataQueryVariables>({
        query: ProductLabelsDataDocument,
        variables: { ids: Array.from(new Set(saved.items.map(item => item.productId))) },
      })
      .catch(() => null);

    setLoadingQueueId(null);

    if (!result) {
      notify({ status: "error", text: intl.formatMessage(messages.queueLoadFailed) });

      return;
    }

    const products = new Map<string, LabelProduct>(
      (mapEdgesToItems(result.data.products) ?? []).map((product: LabelProduct) => [
        product.id,
        product,
      ]),
    );
    const entries: LabelQueueEntry[] = [];

    for (const item of saved.items) {
      const product = products.get(item.productId);
      const variant = product?.variants?.find(candidate => candidate.id === item.variantId);

      if (product && (variant || item.variantId === product.id)) {
        entries.push({ ...hitFromProduct(product, variant), quantity: item.quantity });
      }
    }

    setQueue(previous => {
      if (mode === "replace") {
        return entries;
      }

      const next = [...previous];

      for (const entry of entries) {
        const existing = next.findIndex(candidate => candidate.variantId === entry.variantId);

        if (existing >= 0) {
          next[existing] = {
            ...next[existing],
            quantity: Math.min(MAX_LABEL_COPIES, next[existing].quantity + entry.quantity),
          };
        } else {
          next.push(entry);
        }
      }

      return next;
    });

    if (mode === "replace") {
      setLoadedQueueName(saved.name);
    }

    setSavedQueuesOpen(false);
    notify({
      status: "success",
      text: intl.formatMessage(messages.queueLoaded, { name: saved.name }),
    });

    const missing = saved.items.length - entries.length;

    if (missing > 0) {
      notify({
        status: "warning",
        text: intl.formatMessage(messages.queueMissingItems, { count: missing }),
      });
    }
  };

  const trimmedSaveName = saveName?.trim() ?? "";
  const sameNameQueue = savedQueues.find(
    saved => saved.name.trim().toLocaleLowerCase() === trimmedSaveName.toLocaleLowerCase(),
  );
  const saveNameError = !trimmedSaveName
    ? intl.formatMessage(messages.queueNameRequired)
    : sameNameQueue && !sameNameQueue.canEdit
      ? intl.formatMessage(messages.saveQueueTakenByOther, { name: sameNameQueue.name })
      : undefined;
  const saveNameNote =
    sameNameQueue?.canEdit && trimmedSaveName
      ? intl.formatMessage(messages.saveQueueOverwrite, { name: sameNameQueue.name })
      : intl.formatMessage(messages.saveQueueHint);

  const handleSaveQueue = async () => {
    if (saveNameError) {
      return;
    }

    const result = await saveQueue({
      variables: {
        name: trimmedSaveName,
        items: queue.map(entry => ({
          variantId: entry.variantId,
          productId: entry.productId,
          quantity: entry.quantity,
        })),
      },
    });
    const saved = result.data?.labelQueueSave;

    if (saved?.productErrors.length || !saved?.labelQueue) {
      notify({
        status: "error",
        text: saved?.productErrors[0]?.message ?? intl.formatMessage(messages.queueSaveFailed),
      });

      return;
    }

    setLoadedQueueName(saved.labelQueue.name);
    setSaveName(null);
    await labelQueues.refetch();
    notify({
      status: "success",
      text: intl.formatMessage(messages.queueSaved, { name: saved.labelQueue.name }),
    });
  };

  const handleDeleteQueue = async (saved: SavedLabelQueue) => {
    setDeletingQueueId(saved.id);

    const result = await deleteQueue({ variables: { id: saved.id } });

    setDeletingQueueId(null);

    if (result.data?.labelQueueDelete?.productErrors.length) {
      notify({
        status: "error",
        text: result.data.labelQueueDelete.productErrors[0].message ?? "",
      });

      return;
    }

    if (loadedQueueName === saved.name) {
      setLoadedQueueName(null);
    }

    await labelQueues.refetch();
    notify({
      status: "success",
      text: intl.formatMessage(messages.queueDeleted, { name: saved.name }),
    });
  };

  const pickProduct = arrival?.pickProductId ? productsById.get(arrival.pickProductId) : undefined;
  const pickGroup =
    pickProduct && (pickProduct.variants?.length ?? 0) > 1
      ? {
          productId: pickProduct.id,
          productName: pickProduct.name,
          hits: hitsFromProduct(pickProduct),
        }
      : null;

  const flash = (variantId: string) => {
    setHighlightId(variantId);
    window.setTimeout(
      () => setHighlightId(current => (current === variantId ? null : current)),
      900,
    );
  };

  const addHits = (hits: LabelVariantHit[]) => {
    setQueue(previous => {
      const next = [...previous];

      for (const hit of [...hits].reverse()) {
        const existing = next.findIndex(entry => entry.variantId === hit.variantId);

        if (existing >= 0) {
          if (hits.length === 1) {
            next[existing] = {
              ...next[existing],
              quantity: Math.min(MAX_LABEL_COPIES, next[existing].quantity + 1),
            };
          }
        } else {
          next.unshift({ ...hit, quantity: 1 });
        }
      }

      return next;
    });

    if (hits.length === 1) {
      flash(hits[0].variantId);
      notify({
        status: "success",
        text: intl.formatMessage(messages.added, { sku: hits[0].sku || hits[0].productName }),
      });
    }
  };

  const setQuantity = (variantId: string, quantity: number) =>
    setQueue(previous =>
      previous.map(entry =>
        entry.variantId === variantId
          ? { ...entry, quantity: Math.max(1, Math.min(MAX_LABEL_COPIES, quantity)) }
          : entry,
      ),
    );

  const removeEntry = (variantId: string) =>
    setQueue(previous => previous.filter(entry => entry.variantId !== variantId));

  // Fresh data (e.g. a batch registered here) wins over what the search
  // result carried when the row was added.
  const displayedQueue = queue.map(entry => {
    const product = productsById.get(entry.productId);
    const variant = product?.variants?.find(candidate => candidate.id === entry.variantId);

    return variant ? { ...entry, batchNumber: variant.latestBatchNumber ?? null } : entry;
  });

  const labelItems: LabelData[] = queue.flatMap(entry => {
    const product = productsById.get(entry.productId);

    if (!product) {
      return [];
    }

    const variant = product.variants?.find(candidate => candidate.id === entry.variantId);

    if (!variant && entry.variantId !== product.id) {
      return [];
    }

    const item = toLabelData(product, variant);

    return Array.from({ length: entry.quantity }, () => item);
  });

  // Registering a batch records where/when goods arrived, so it's an admin
  // task: operators only ever see (and print) the code an admin registered.
  const handleRegisterBatch = async (values: BatchFormValues) => {
    if (!batchDialogEntry) {
      return;
    }

    const result = await mintBatchNumber({
      variables: {
        variantId: batchDialogEntry.variantId,
        arrivalAt: new Date(values.arrivalAt).toISOString(),
        isEstimated: values.isEstimated,
        supplier: values.supplier,
        reference: values.reference,
        quantity: values.quantity === "" ? undefined : Number(values.quantity),
        notes: values.notes,
      },
    });
    const errors = result.data?.variantBatchNumberCreate?.productErrors ?? [];

    if (errors.length > 0) {
      setBatchError(
        intl.formatMessage(
          errors.some(error => error.code === "UNIQUE")
            ? batchMessages.duplicateShipment
            : batchMessages.genericError,
        ),
      );

      return;
    }

    setBatchError(undefined);
    setBatchDialogEntry(null);
    await refetch();
  };

  const queueCard = (
    <DashboardCard
      borderWidth={1}
      borderStyle="solid"
      borderColor="default1"
      borderRadius={4}
      paddingBottom={4}
    >
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(messages.whatToPrintTitle)}</DashboardCard.Title>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            variant={isSavedQueuesOpen ? "secondary" : "tertiary"}
            size="small"
            aria-expanded={isSavedQueuesOpen}
            onClick={() => setSavedQueuesOpen(open => !open)}
            data-test-id="toggle-saved-queues"
          >
            {intl.formatMessage(messages.savedQueuesButton, { count: savedQueues.length })}
            {isSavedQueuesOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </Button>
          {isAdmin && (
            <Button
              variant="tertiary"
              size="small"
              onClick={() => history.push(labelPrintingBatchesPath)}
            >
              {intl.formatMessage(batchMessages.openBatches)}
            </Button>
          )}
        </Box>
      </DashboardCard.Header>
      {isSavedQueuesOpen && (
        <Box paddingX={6} paddingBottom={4}>
          <LabelSavedQueues
            queues={savedQueues}
            loading={labelQueues.loading}
            deletingId={deletingQueueId}
            loadingId={loadingQueueId}
            onOpen={saved => openSavedQueue(saved, "replace")}
            onAppend={saved => openSavedQueue(saved, "append")}
            onDelete={handleDeleteQueue}
          />
        </Box>
      )}
      <DashboardCard.Content className="labelWhatGrid">
        <LabelVariantSearch
          queuedCounts={Object.fromEntries(queue.map(entry => [entry.variantId, entry.quantity]))}
          onAdd={hit => addHits([hit])}
          onAddMany={addHits}
          pickGroup={pickGroup}
        />
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
            <Text size={3} fontWeight="bold">
              {intl.formatMessage(messages.queueTitle)}
              {queue.length > 0 && (
                <Text size={3} color="default2" fontWeight="regular">
                  {" · "}
                  {intl.formatMessage(messages.queueCount, { count: queue.length })}
                </Text>
              )}
              {loadedQueueName && queue.length > 0 && (
                <Text size={3} color="default2" fontWeight="regular">
                  {" · "}
                  {intl.formatMessage(messages.loadedQueueName, { name: loadedQueueName })}
                </Text>
              )}
            </Text>
            <Box display="flex" alignItems="center" gap={2}>
              <Text size={1} color="default2">
                {intl.formatMessage(messages.queueQuantityHint)}
              </Text>
              {queue.length > 0 && (
                <Button
                  variant="secondary"
                  size="small"
                  icon={<Save size={14} />}
                  onClick={() => setSaveName(loadedQueueName ?? "")}
                  data-test-id="save-queue"
                >
                  {intl.formatMessage(messages.saveQueue)}
                </Button>
              )}
              {queue.length > 0 && (
                <Button
                  variant="tertiary"
                  size="small"
                  onClick={() => {
                    setQueue([]);
                    setLoadedQueueName(null);
                  }}
                >
                  {intl.formatMessage(messages.clearQueue)}
                </Button>
              )}
            </Box>
          </Box>
          <LabelQueue
            entries={displayedQueue}
            highlightId={highlightId}
            onQuantityChange={setQuantity}
            onRemove={removeEntry}
            onRegisterBatch={
              isAdmin
                ? entry => {
                    setBatchError(undefined);
                    setBatchDialogEntry(entry);
                  }
                : undefined
            }
          />
        </Box>
      </DashboardCard.Content>
    </DashboardCard>
  );

  return (
    <>
      <LabelPrintDialog
        title={intl.formatMessage(messages.pageTitle)}
        items={labelItems}
        topContent={queueCard}
        onBack={arrival ? () => history.goBack() : undefined}
        onOpenHelp={() => history.push(labelPrintingHelpPath)}
      />
      {saveName !== null && (
        <DashboardModal onChange={() => setSaveName(null)} open>
          <DashboardModal.Content size="sm">
            <DashboardModal.Header>
              {intl.formatMessage(messages.saveQueueTitle)}
            </DashboardModal.Header>
            <Input
              label={intl.formatMessage(messages.queueNameLabel)}
              value={saveName}
              autoFocus
              onChange={event => setSaveName(event.target.value)}
              onKeyDown={event => {
                if (event.key === "Enter") {
                  handleSaveQueue();
                }
              }}
              error={!!trimmedSaveName && !!saveNameError}
              helperText={trimmedSaveName ? (saveNameError ?? saveNameNote) : saveNameNote}
              data-test-id="queue-name"
            />
            <DashboardModal.Actions>
              <Button variant="secondary" onClick={() => setSaveName(null)}>
                {intl.formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="primary"
                disabled={!!saveNameError || saveQueueResult.loading}
                onClick={handleSaveQueue}
                data-test-id="confirm-save-queue"
              >
                {intl.formatMessage(messages.saveQueue)}
              </Button>
            </DashboardModal.Actions>
          </DashboardModal.Content>
        </DashboardModal>
      )}
      {batchDialogEntry && (
        <BatchFormDialog
          open
          mode="create"
          subtitle={[
            batchDialogEntry.productName,
            batchDialogEntry.variantName,
            batchDialogEntry.sku,
          ]
            .filter(Boolean)
            .join(" — ")}
          loading={mintBatchNumberResult.loading}
          error={batchError}
          initialValues={{
            arrivalAt: toDateTimeLocalValue(new Date()),
            isEstimated: false,
            supplier: "",
            reference: "",
            quantity: "",
            notes: "",
          }}
          onClose={() => setBatchDialogEntry(null)}
          onSubmit={(values: BatchFormValues) => handleRegisterBatch(values)}
        />
      )}
    </>
  );
};
